/**
 * HeroCarousel.jsx
 * Dynamic Slides API Version
 */

import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../helpers";

export default function HeroCarousel({ onBookClick }) {
  const getImageUrl = (img) => {
    if (!img) return "";
    const clean = img.replace(/\\/g, "/");
    if (clean.startsWith("http")) return clean;
    const baseUrl =process.env.REACT_APP_WEB_URL;
    return `${baseUrl}/${clean.replace(/^\/+/, "")}`;
  };
  let bg = "linear-gradient(135deg, #0A1628 0%, #1B2A6B 50%, #0A1628 100%)";
  let accent = "#1D9E75";
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      const json = await apiFetch(`${process.env.REACT_APP_SLIDE_VIEW}`);

      if (json.status) {
        const rows = (json.data || []).filter((x) => x.status === "Active");

        setSlides(rows);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const goTo = useCallback(
    (idx) => {
      if (animating || slides.length === 0) return;

      setAnimating(true);
      setCurrent(idx);

      setTimeout(() => setAnimating(false), 500);
    },
    [animating, slides],
  );

  useEffect(() => {
    if (slides.length === 0) return;

    const t = setInterval(() => {
      goTo((current + 1) % slides.length);
    }, 5000);

    return () => clearInterval(t);
  }, [current, slides, goTo]);

  if (slides.length === 0) return null;

  const slide = slides[current];

  return (
    <section
      id="home"
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: bg,
        transition: "background 0.8s ease",
        overflow: "hidden",
        fontFamily: "'Nunito','Segoe UI',sans-serif",
      }}
    >
      <div style={{
        padding: 40,
      }}></div>
      {/* BG */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `${accent}10`,
            top: -100,
            right: -100,
            animation: "pulse 6s ease-in-out infinite",
          }}
        />

        <div
          style={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `${accent}08`,
            bottom: -50,
            left: -50,
            animation: "pulse 8s ease-in-out infinite 2s",
          }}
        />
      </div>

      <style>{`
        @keyframes pulse {
          0%,100% {transform:scale(1);opacity:.6}
          50% {transform:scale(1.1);opacity:.9}
        }
        @keyframes fadeUp {
          from {opacity:0;transform:translateY(30px)}
          to {opacity:1;transform:translateY(0)}
        }
      `}</style>

      {/* Content */}
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "100px 1.5rem 60px",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Badge */}
        <div
          key={current + "badge"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: `${accent}20`,
            border: `1px solid ${accent}40`,
            borderRadius: 20,
            padding: "6px 18px",
            fontSize: 13,
            fontWeight: 600,
            color: accent,
            marginBottom: 20,
            animation: "fadeUp .6s ease",
          }}
        >
          {slide.image && (
            <img
              src={getImageUrl(slide.image)}
              alt={slide.title}
              style={{
                maxWidth: "420px",
                width: "100%",
                borderRadius: 18,
                boxShadow: "0 20px 40px rgba(0,0,0,.25)",
              }}
            />
          )}
        </div>

        {/* Title */}
        <h1
          key={current + "title"}
          style={{
            fontSize: "clamp(2rem,5vw,3.5rem)",
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1.15,
            marginBottom: 20,
            animation: "fadeUp .6s ease .1s both",
          }}
        >
          {slide.title}
        </h1>

        {/* Subtitle */}
        <p
          key={current + "sub"}
          style={{
            fontSize: "clamp(1rem,2vw,1.15rem)",
            color: "rgba(255,255,255,.75)",
            maxWidth: 650,
            margin: "0 auto 36px",
            lineHeight: 1.7,
            animation: "fadeUp .6s ease .2s both",
          }}
        >
          {slide.subtitle}
        </p>

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            gap: 14,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={onBookClick}
            style={{
              background: "#E8323C",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "14px 32px",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            📅 Book Appointment
          </button>

          <a
            href="#services"
            style={{
              background: "transparent",
              color: "#fff",
              border: "2px solid rgba(255,255,255,.35)",
              borderRadius: 10,
              padding: "13px 32px",
              fontSize: 15,
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            Our Services
          </a>
        </div>
      </div>

      {/* Dots */}
      <div
        style={{
          position: "absolute",
          bottom: 28,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 8,
        }}
      >
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            style={{
              width: i === current ? 28 : 8,
              height: 8,
              borderRadius: 4,
              background: i === current ? accent : "rgba(255,255,255,.35)",
              border: "none",
              cursor: "pointer",
            }}
          />
        ))}
      </div>
      {/* Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={() => goTo((current - 1 + slides.length) % slides.length)}
            className="d-none d-md-flex"
            style={{
              position: "absolute",
              top: "50%",
              left: 20,
              transform: "translateY(-50%)",
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "rgba(255,255,255,.1)",
              border: "1px solid rgba(255,255,255,.2)",
              color: "#fff",
              fontSize: 22,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3,
            }}
          >
            ‹
          </button>

          <button
            onClick={() => goTo((current + 1) % slides.length)}
            className="d-none d-md-flex"
            style={{
              position: "absolute",
              top: "50%",
              right: 20,
              transform: "translateY(-50%)",
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "rgba(255,255,255,.1)",
              border: "1px solid rgba(255,255,255,.2)",
              color: "#fff",
              fontSize: 22,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3,
            }}
          >
            ›
          </button>
        </>
      )}
    </section>
  );
}
